import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowRight, Ruler, Palette, Brush, Image, Camera, FileText } from "lucide-react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import TextReveal from "@/components/TextReveal";
import MagneticButton from "@/components/MagneticButton";
import FAQSection, { useFAQs } from "@/components/FAQSection";
import AnswerBlock from "@/components/AnswerBlock";

import prestaArchitecture from "@/assets/presta-architecture.jpg";
import prestaCouleurs from "@/assets/presta-decoration.jpg";
import prestaFresque from "@/assets/presta-fresque.jpg";

const prestations = [
  {
    icon: Ruler,
    image: prestaArchitecture,
    title: "Architecture d'intérieur",
    price: "Sur devis\nÀ partir de 1 500€",
    intro: "Repenser un lieu, du plan jusqu'à la dernière poignée",
    audience: "Particuliers & professionnels - commerces, hôtellerie, restauration, bureaux",
    description: "Certains arrivent avec un cahier des charges complet, d'autres avec une photo et l'impression que quelque chose cloche. Les deux se travaillent.\n\nJe pars de vos contraintes réelles, budget compris et je vous propose une organisation de l'espace que vous pourrez juger sur plans et en 3D avant d'engager le moindre euro de travaux.",
    includes: [
      "Visuels 3D photoréalistes pour valider chaque décision avant les travaux",
      "Plans d'aménagement et plans techniques détaillés (éclairage, menuiserie, élévation)",
      "Palette chromatique personnalisée et sélection des matières",
      "Mobilier sur mesure conçu pour cet espace",
      "Coordination des artisans et suivi de chantier jusqu'à la livraison",
    ],
    steps: [
      { title: "Visite et relevé", desc: "Je mesure, je photographie, je note ce qui vous gêne au quotidien. On cadre le budget dès ce rendez-vous." },
      { title: "Recherche", desc: "Moodboards et premières implantations.\nDeux propositions : la vôtre et une autre à laquelle vous n'aviez pas pensé." },
      { title: "Plans et 3D", desc: "Plans cotés, rendus 3D, choix des matières et dessins du mobilier sur mesure." },
      { title: "Chantier et réception", desc: "Consultation des artisans, visites régulières, tour final avec liste de réserves." },
    ],
  },
  {
    icon: Palette,
    image: prestaCouleurs,
    title: "Décoration & conseil couleurs",
    price: "À partir de 300€",
    intro: "Les bonnes teintes, testées chez vous",
    audience: null,
    description: "Une teinte se comporte différemment selon l'orientation de la pièce, la couleur du sol et l'ampoule installée au plafond. C'est pour cette raison que je travaille avec des échantillons posés sur vos murs, observés le matin puis en fin de journée, plutôt qu'avec un nuancier consulté en boutique.\n\nCe rendez-vous convient bien à ceux qui ont déjà des idées mais n'arrivent pas à les faire tenir ensemble.",
    includes: [
      "Consultation à domicile ou en visioconférence",
      "Travail ciblé sur 1 à 2 pièces",
      "Palette de couleurs personnalisée avec références exactes",
      "Harmonisation avec vos matériaux, sols et mobilier existants",
      "Croquis et recommandations réalisés en direct",
      "Compte-rendu écrit avec récapitulatif des préconisations",
    ],
    steps: [
      { title: "Prise de contact", desc: "Échange pour comprendre votre espace et vos attentes." },
      { title: "Consultation", desc: "2h dédiées à votre projet, avec croquis et conseils en direct." },
      { title: "Compte-rendu", desc: "Dossier synthétique avec la palette finale et les préconisations." },
    ],
  },
  {
    icon: Brush,
    image: prestaFresque,
    focal: "top" as const,
    title: "Fresques murales",
    price: "Sur devis",
    intro: "De l'art sur vos murs, peint à la main",
    audience: null,
    description: "Une fresque coûte souvent moins cher qu'un revêtement mural haut de gamme et personne d'autre ne l'aura. Je la dessine pour le mur en question, en tenant compte de la lumière qui le traverse et du recul dont on dispose pour la regarder.\n\nEn intérieur, en vitrine ou en façade, avec des peintures adaptées au support et à l'exposition.",
    includes: [
      "Conception sur mesure adaptée à votre espace",
      "Réalisation entièrement peinte à la main",
      "Applicable en intérieur, en vitrine ou en façade extérieure",
    ],
    steps: [
      { title: "Brief & conception", desc: "Échange sur votre univers, l'espace et l'ambiance souhaitée.\nProposition visuelle avant réalisation." },
      { title: "Validation", desc: "Présentation du concept pour approbation." },
      { title: "Réalisation", desc: "Exécution sur site, entièrement à la main." },
    ],
  },
];

const autresPrestations = [
  { icon: Image, title: "Images 3D pour architectes & promoteurs", desc: "Perspectives photoréalistes pour vos concours, dossiers de promotion et communication visuelle." },
  { icon: Camera, title: "Conseils achat immobilier", desc: "Une visite avec vous avant de signer.\nCe qui est faisable, ce qui ne l'est pas et un ordre de grandeur des travaux." },
  { icon: FileText, title: "Dossiers administratifs", desc: "Déclaration préalable, permis de construire, plans techniques." },
];

const Prestations = () => {
  const { faqs } = useFAQs();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [navHidden, setNavHidden] = useState(false);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    setNavHidden(latest > previous && latest > 150);
  });

  useEffect(() => {
    const ids = [...prestations.map((p) => p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")), "et-aussi---"];
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Pick the one with the highest intersection ratio
          visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
          const first = visible[0];
          if (first) setActiveId(first.target.id);
        }
      },
      { rootMargin: "-30% 0px -50% 0px", threshold: [0, 0.25, 0.5] }
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const faqJsonLd = faqs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Architecture d'intérieur - JIIA Studio",
      provider: { "@type": "LocalBusiness", name: "JIIA Studio" },
      areaServed: { "@type": "Place", name: "Région Rhône-Alpes" },
      description: "Architecture d'intérieur, images d'architecture, conseils déco, colorimétrie, fresques murales, conseils achat immobilier.",
      serviceType: "Architecture d'intérieur",
    },
    ...(faqJsonLd ? [faqJsonLd] : []),
  ];

  return (
    <Layout>
      <SEOHead
        title="Prestations | JIIA Studio Architecte d'intérieur Lyon"
        description="Découvrez les prestations de JIIA Studio : architecture d'intérieur, images 3D, conseils déco, colorimétrie, fresques murales, conseils achat immobilier. Région Rhône-Alpes."
        canonical="/prestations"
        jsonLd={jsonLd}
      />

      {/* Header */}
      <section className="pt-24 md:pt-32 pb-10">
        <div className="container max-w-3xl text-center">
          <AnimatedSection direction="none" scale>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">Prestations</p>
          </AnimatedSection>
          <TextReveal delay={0.1}>
            <h1 className="font-heading text-4xl md:text-5xl font-light mb-6">
              Prestations et tarifs
            </h1>
          </TextReveal>
        </div>
      </section>

      <div className="pb-8">
        <AnswerBlock
          summary={"JIIA Studio accompagne particuliers et professionnels à Lyon et en Rhône-Alpes, de la conception à la réception de chantier. Trois prestations principales, plus les images 3D, les conseils achat et les dossiers administratifs."}
          facts={[
            "Architecture d'intérieur : à partir de 1 500€",
            "Lyon, Ouest lyonnais et Rhône-Alpes",
            "Fresques murales : sur devis",
            "Premier rendez-vous offert",
            "Décoration et conseil couleurs : à partir de 300€",
          ]}
        />
      </div>

      <motion.nav
        animate={{ top: navHidden ? 0 : 80 }}
        transition={{ duration: 0.35, ease: [0.33, 1, 0.68, 1] }}
        className="sticky z-40 bg-background/80 backdrop-blur-md border-b border-border/50 py-3"
        style={{ top: 80 }}
      >
        <div className="container max-w-5xl overflow-x-auto scrollbar-hide">
          <div className="flex items-center gap-2 min-w-max">
            {prestations.map((p) => {
              const id = p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              const isActive = activeId === id;
              return (
                <button
                  key={p.title}
                  onClick={() => {
                    const el = document.getElementById(id);
                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs font-body uppercase tracking-wider rounded-full transition-all duration-300 whitespace-nowrap ${
                    isActive
                      ? "bg-accent text-accent-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                  }`}
                >
                  <p.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {p.title}
                </button>
              );
            })}
            <button
              onClick={() => {
                const el = document.getElementById("et-aussi---");
                el?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`flex items-center gap-1.5 px-4 py-2 text-xs font-body uppercase tracking-wider rounded-full transition-all duration-300 whitespace-nowrap ${
                activeId === "et-aussi---"
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <FileText className="h-3.5 w-3.5" strokeWidth={1.5} />
              Autres prestations
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Prestations détaillées */}
      {prestations.map((prestation, index) => (
        <section
          key={prestation.title}
          id={prestation.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
          className={`py-20 md:py-28 scroll-mt-32 ${index % 2 === 0 ? "bg-secondary" : ""}`}
        >
          <div className="container max-w-5xl">
            {/* Image banner */}
            <AnimatedSection direction="none" scale className="mb-12">
              <motion.div
                className="relative w-full aspect-[21/9] overflow-hidden rounded-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <img
                  src={prestation.image}
                  alt={prestation.title}
                  className={`w-full h-full object-cover ${(prestation as any).focal === "top" ? "object-top" : ""}`}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(36,30%,85%,0.6)] to-transparent" />
              </motion.div>
            </AnimatedSection>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
              {/* Left: titre + prix */}
              <div className="lg:col-span-2">
                <AnimatedSection direction="left">
                  <motion.div
                    whileHover={{ rotate: -10, scale: 1.1 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="inline-block"
                  >
                    <prestation.icon className="h-8 w-8 text-accent mb-4" strokeWidth={1.5} />
                  </motion.div>
                  <h2 className="font-heading text-3xl md:text-4xl font-light mb-3">
                    {prestation.title}
                  </h2>
                  <p className="text-accent font-body font-medium text-sm uppercase tracking-wider whitespace-pre-line">
                    {prestation.price}
                  </p>
                  {prestation.audience && (
                    <p className="text-muted-foreground font-body text-sm mt-3 leading-relaxed">
                      {prestation.audience}
                    </p>
                  )}
                </AnimatedSection>
              </div>

              {/* Right: description + includes + steps */}
              <div className="lg:col-span-3 space-y-8">
                <AnimatedSection direction="right" delay={0.1}>
                  <p className="font-heading text-xl italic text-foreground/80 mb-4">
                    {prestation.intro}
                  </p>
                  <p className="text-muted-foreground font-body leading-relaxed whitespace-pre-line">
                    {prestation.description}
                  </p>
                </AnimatedSection>

                {prestation.includes && (
                  <AnimatedSection delay={0.2}>
                    <h3 className="font-heading text-lg mb-4">Ce que comprend la prestation</h3>
                    <ul className="space-y-3">
                      {prestation.includes.map((item, i) => (
                        <motion.li
                          key={i}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                          className="flex items-start gap-3 text-sm font-body text-muted-foreground"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-accent mt-1.5 flex-shrink-0" />
                          {item}
                        </motion.li>
                      ))}
                    </ul>
                  </AnimatedSection>
                )}

                {prestation.steps && (
                  <AnimatedSection delay={0.3}>
                    <h3 className="font-heading text-lg mb-6">Déroulé</h3>
                    <div className="space-y-0">
                      {prestation.steps.map((step, i) => (
                        <motion.div
                          key={i}
                          initial={{ opacity: 0, y: 15 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.4 + i * 0.1, duration: 0.5 }}
                          className="flex gap-4 pb-6 last:pb-0"
                        >
                          <div className="flex flex-col items-center">
                            <span className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center text-xs font-body font-medium flex-shrink-0">
                              {i + 1}
                            </span>
                            {i < prestation.steps!.length - 1 && (
                              <div className="w-px flex-1 bg-border mt-2" />
                            )}
                          </div>
                          <div className="pt-1">
                            <p className="font-heading text-base font-medium mb-1">{step.title}</p>
                            <p className="text-sm text-muted-foreground font-body leading-relaxed whitespace-pre-line">{step.desc}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </AnimatedSection>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Autres prestations */}
      <section className="py-20 md:py-28 bg-secondary scroll-mt-32" id="et-aussi---">
        <div className="container max-w-5xl">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Et aussi</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              Autres prestations
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            {autresPrestations.map((item, i) => (
              <AnimatedSection key={item.title} delay={i * 0.1}>
                <div className="p-6 border border-border rounded-sm bg-background h-full">
                  <item.icon className="h-6 w-6 text-accent mb-4" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed whitespace-pre-line">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection delay={0.3}>
            <p className="text-muted-foreground font-body text-sm">
              Toutes ces prestations sont sur devis.{" "}
              <Link to="/contact" className="text-accent underline underline-offset-4 hover:text-accent/80 transition-colors">
                Contactez-moi pour en discuter.
              </Link>
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}

      <section className="py-16 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-4">
              En savoir plus
            </p>
            <h3 className="font-heading text-2xl font-light mb-8">
              Explorez mes spécialités
            </h3>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Rénovation intérieure Lyon", href: "/renovation-interieure-lyon" },
              { label: "Aménagement pro & hôtellerie", href: "/amenagement-professionnel-hotellerie" },
              { label: "Images 3D & visualisation", href: "/images-3d-visualisation-architecturale" },
              { label: "Décoration intérieure Lyon", href: "/decoration-interieure-lyon" },
            ].map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-center justify-between p-4 bg-background rounded-sm border border-border hover:border-accent/50 transition-colors group"
              >
                <span className="font-body text-sm group-hover:text-accent transition-colors">{link.label}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FAQSection />

      {/* CTA */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground">
        <div className="container text-center max-w-2xl">
          <AnimatedSection direction="none" scale>
            <TextReveal>
              <h2 className="font-heading text-3xl md:text-5xl font-light mb-6">
                Un projet en tête ?
              </h2>
            </TextReveal>
            <AnimatedSection delay={0.2}>
              <p className="text-primary-foreground/70 font-body mb-8">
                Chaque projet est unique. Contactez-moi pour un premier échange gratuit et sans engagement.
              </p>
            </AnimatedSection>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <MagneticButton className="inline-block">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-3 px-8 py-4 border border-primary-foreground/30 text-sm font-body uppercase tracking-widest hover:bg-primary-foreground hover:text-primary transition-all duration-500"
                >
                  Me contacter <ArrowRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default Prestations;
